import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { CheckCircle2, XCircle, AlertCircle, Camera, ArrowLeft, Ticket, CameraOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESET_MS = 3500;

const S = { IDLE: 'idle', SCANNING: 'scanning', LOADING: 'loading', VALID: 'valid', USED: 'used', CANCELLED: 'cancelled', INVALID: 'invalid', ERROR: 'error' };

function extractTicketId(text) {
  try {
    const url = new URL(text);
    const parts = url.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1];
  } catch {
    return text.trim();
  }
}

export default function EventCheckIn() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [eventName, setEventName] = useState('');
  const [state, setState] = useState(S.IDLE);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const cooldownRef = useRef(false);
  const resetTimerRef = useRef(null);
  const processingRef = useRef(false);

  // Load event name
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEventName(r.data.name))
      .catch(() => {});
  }, [eventId, token]);

  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      clearTimeout(resetTimerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const resetToScanning = useCallback(() => {
    cooldownRef.current = false;
    processingRef.current = false;
    setResult(null);
    setState(S.SCANNING);
  }, []);

  const handleScannedCode = useCallback(async (text) => {
    if (cooldownRef.current || processingRef.current) return;
    cooldownRef.current = true;
    processingRef.current = true;
    setState(S.LOADING);

    const ticketId = extractTicketId(text);
    if (!ticketId || ticketId.length < 10) {
      setState(S.INVALID);
      setErrorMsg('Could not read a valid ticket from this QR code.');
      resetTimerRef.current = setTimeout(resetToScanning, RESET_MS);
      return;
    }

    try {
      const lookup = await axios.get(`${API}/events/scan/${ticketId}`);
      const { ticket, event: ev, booking, cancelled, cancel_reason } = lookup.data;

      if (cancelled) {
        setState(S.CANCELLED);
        setResult({ name: booking.customer_name, num: ticket.ticket_number, total: booking.ticket_count, eventName: ev.name, reason: cancel_reason });
      } else if (ticket.checked_in) {
        setState(S.USED);
        setResult({ name: booking.customer_name, num: ticket.ticket_number, total: booking.ticket_count, at: ticket.checked_in_at, eventName: ev.name });
      } else {
        await axios.post(
          `${API}/admin/events/${ev.id}/tickets/${ticketId}/check-in`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setState(S.VALID);
        setResult({ name: booking.customer_name, num: ticket.ticket_number, total: booking.ticket_count, eventName: ev.name });
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setState(S.INVALID);
        setErrorMsg('This QR code is not a valid ticket.');
      } else if (err.response?.status === 409) {
        setState(S.CANCELLED);
        setResult(null);
        setErrorMsg(err.response?.data?.detail || 'This ticket has been cancelled.');
      } else {
        setState(S.ERROR);
        setErrorMsg(err.response?.data?.detail || 'Check-in failed. Check your connection.');
      }
    }

    resetTimerRef.current = setTimeout(resetToScanning, RESET_MS);
  }, [eventId, token, resetToScanning]);

  // Main scan loop — reads video frames into canvas and runs jsQR
  const scanLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    if (!cooldownRef.current && canvas.width > 0) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
      if (code && code.data) {
        handleScannedCode(code.data);
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [handleScannedCode]);

  const startCamera = async () => {
    setCameraError('');
    setState(S.SCANNING);
    try {
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        await video.play();
        rafRef.current = requestAnimationFrame(scanLoop);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setState(S.IDLE);
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const isAndroid = /android/i.test(navigator.userAgent);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (isIOS) {
          setCameraError('ios-denied');
        } else if (isAndroid) {
          setCameraError('android-denied');
        } else {
          setCameraError('desktop-denied');
        }
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setCameraError('Camera is in use by another app. Close other apps and try again.');
      } else {
        setCameraError(`Camera error: ${err.message || err.name}. Try refreshing the page.`);
      }
    }
  };

  const bgColors = {
    [S.IDLE]: 'bg-slate-950',
    [S.SCANNING]: 'bg-slate-950',
    [S.LOADING]: 'bg-slate-950',
    [S.VALID]: 'bg-emerald-950',
    [S.USED]: 'bg-amber-950',
    [S.INVALID]: 'bg-red-950',
    [S.ERROR]: 'bg-red-950',
  };

  const isLive = state === S.SCANNING || state === S.LOADING || state === S.VALID || state === S.USED || state === S.INVALID;

  return (
    <div className={`min-h-screen ${bgColors[state] || 'bg-slate-950'} text-white flex flex-col transition-colors duration-300`}>

      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-white/10 flex-shrink-0">
        <button onClick={() => { stopCamera(); navigate('/admin/events'); }} className="text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/40 font-semibold uppercase tracking-wider">Door Scanner</p>
          <p className="text-sm font-bold truncate">{eventName || 'Event Check-in'}</p>
        </div>
        <Ticket className="h-5 w-5 text-white/20" />
      </div>

      {/* Camera viewport — always in DOM so video element is always mounted */}
      <div className="relative flex-shrink-0 bg-black" style={{ minHeight: 260 }}>
        <video
          ref={videoRef}
          className="w-full object-cover"
          style={{ maxHeight: 360, display: isLive ? 'block' : 'none' }}
          playsInline
          muted
        />
        {/* Hidden canvas used for frame capture — never shown */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Viewfinder overlay */}
        {(state === S.SCANNING || state === S.LOADING) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-52 h-52 relative">
              {/* Corner markers */}
              {['top-0 left-0 border-t-2 border-l-2 rounded-tl-lg',
                'top-0 right-0 border-t-2 border-r-2 rounded-tr-lg',
                'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg',
                'bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg'
              ].map((cls, i) => (
                <div key={i} className={`absolute w-8 h-8 border-white/70 ${cls}`} />
              ))}
              {state === S.LOADING && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Result / idle panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">

        {state === S.IDLE && (
          <div className="w-full max-w-sm space-y-4">
            {!cameraError && (
              <>
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto">
                  <Camera className="h-8 w-8 text-white/40" />
                </div>
                <button
                  onClick={startCamera}
                  className="w-full bg-white text-slate-900 font-bold py-3.5 rounded-2xl hover:bg-white/90 active:scale-95 transition-all text-sm"
                >
                  Start camera
                </button>
                <p className="text-xs text-white/30 text-center">
                  Point your camera at a guest's QR ticket to check them in
                </p>
              </>
            )}

            {cameraError === 'ios-denied' && (
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-400/10 flex items-center justify-center mx-auto">
                  <CameraOff className="h-8 w-8 text-red-400" />
                </div>
                <p className="text-red-300 font-bold text-center">Camera permission blocked</p>
                <div className="bg-white/5 rounded-2xl p-4 text-left space-y-2">
                  <p className="text-white/70 text-sm font-semibold mb-2">To fix this on iPhone / iPad:</p>
                  <div className="space-y-2 text-sm text-white/60">
                    <p>1. Open the iPhone <span className="text-white font-medium">Settings</span> app</p>
                    <p>2. Scroll down and tap <span className="text-white font-medium">Safari</span></p>
                    <p>3. Tap <span className="text-white font-medium">Camera</span></p>
                    <p>4. Select <span className="text-white font-medium">Allow</span></p>
                    <p>5. Come back here and tap the button below</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCameraError(''); startCamera(); }}
                  className="w-full bg-white text-slate-900 font-bold py-3.5 rounded-2xl hover:bg-white/90 active:scale-95 transition-all text-sm"
                >
                  I've allowed it — try again
                </button>
              </div>
            )}

            {cameraError === 'android-denied' && (
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-400/10 flex items-center justify-center mx-auto">
                  <CameraOff className="h-8 w-8 text-red-400" />
                </div>
                <p className="text-red-300 font-bold text-center">Camera permission blocked</p>
                <div className="bg-white/5 rounded-2xl p-4 text-left space-y-2">
                  <p className="text-white/70 text-sm font-semibold mb-2">To fix this on Android:</p>
                  <div className="space-y-2 text-sm text-white/60">
                    <p>1. Tap the <span className="text-white font-medium">lock icon 🔒</span> in the address bar</p>
                    <p>2. Tap <span className="text-white font-medium">Permissions</span></p>
                    <p>3. Set <span className="text-white font-medium">Camera</span> to Allow</p>
                    <p>4. Refresh the page and tap Start camera</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCameraError(''); startCamera(); }}
                  className="w-full bg-white text-slate-900 font-bold py-3.5 rounded-2xl hover:bg-white/90 active:scale-95 transition-all text-sm"
                >
                  I've allowed it — try again
                </button>
              </div>
            )}

            {cameraError === 'desktop-denied' && (
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-400/10 flex items-center justify-center mx-auto">
                  <CameraOff className="h-8 w-8 text-red-400" />
                </div>
                <p className="text-red-300 font-bold text-center">Camera permission blocked</p>
                <div className="bg-white/5 rounded-2xl p-4 text-left">
                  <p className="text-white/70 text-sm font-semibold mb-2">To fix this:</p>
                  <div className="space-y-2 text-sm text-white/60">
                    <p>1. Click the <span className="text-white font-medium">lock icon 🔒</span> in the address bar</p>
                    <p>2. Find <span className="text-white font-medium">Camera</span> and set it to <span className="text-white font-medium">Allow</span></p>
                    <p>3. Refresh the page and try again</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCameraError(''); startCamera(); }}
                  className="w-full bg-white text-slate-900 font-bold py-3.5 rounded-2xl hover:bg-white/90 active:scale-95 transition-all text-sm"
                >
                  I've allowed it — try again
                </button>
              </div>
            )}

            {/* Generic camera errors (not permission related) */}
            {cameraError && !['ios-denied','android-denied','desktop-denied'].includes(cameraError) && (
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-400/10 flex items-center justify-center mx-auto">
                  <CameraOff className="h-8 w-8 text-red-400" />
                </div>
                <p className="text-red-300 text-sm text-center leading-relaxed">{cameraError}</p>
                <button
                  onClick={() => { setCameraError(''); startCamera(); }}
                  className="w-full bg-white text-slate-900 font-bold py-3.5 rounded-2xl hover:bg-white/90 active:scale-95 transition-all text-sm"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}

        {state === S.SCANNING && (
          <p className="text-white/30 text-sm">Point camera at a QR code…</p>
        )}

        {state === S.LOADING && (
          <p className="text-white/50 text-sm animate-pulse">Checking ticket…</p>
        )}

        {state === S.VALID && result && (
          <div className="w-full max-w-sm space-y-3">
            <div className="w-20 h-20 rounded-full bg-emerald-400/15 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <div>
              <p className="text-4xl font-black text-emerald-400 mb-1">Valid!</p>
              <p className="text-2xl font-bold text-white">{result.name}</p>
              <p className="text-emerald-300/60 text-sm mt-1">
                Ticket {result.num} of {result.total}
                {result.eventName && result.eventName !== eventName ? ` · ${result.eventName}` : ''}
              </p>
            </div>
            <p className="text-emerald-400/30 text-xs">Scanning again shortly…</p>
          </div>
        )}

        {state === S.USED && result && (
          <div className="w-full max-w-sm space-y-3">
            <div className="w-20 h-20 rounded-full bg-amber-400/15 flex items-center justify-center mx-auto">
              <AlertCircle className="h-10 w-10 text-amber-400" />
            </div>
            <div>
              <p className="text-3xl font-black text-amber-400 mb-1">Already scanned</p>
              <p className="text-xl font-bold text-white">{result.name}</p>
              <p className="text-amber-300/60 text-sm mt-1">
                Ticket {result.num} of {result.total}
              </p>
              {result.at && (
                <p className="text-amber-300/40 text-xs mt-1">
                  Scanned at {new Date(result.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <p className="text-amber-400/30 text-xs">Scanning again shortly…</p>
          </div>
        )}

        {state === S.CANCELLED && (
          <div className="w-full max-w-sm space-y-3">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto border-2 border-red-500/40">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <div>
              <p className="text-3xl font-black text-red-400 mb-2">Cancelled ticket</p>
              {result && <p className="text-xl font-bold text-white mb-1">{result.name}</p>}
              {result && (
                <p className="text-red-300/60 text-sm">Ticket {result.num} of {result.total}</p>
              )}
              <p className="text-red-300/80 text-sm mt-2 bg-red-500/10 rounded-xl px-4 py-2 border border-red-500/20">
                {result?.reason || errorMsg || 'This ticket has been cancelled and is no longer valid.'}
              </p>
              <p className="text-red-400/50 text-xs mt-2">Do not allow entry.</p>
            </div>
            <p className="text-red-400/30 text-xs">Scanning again shortly…</p>
          </div>
        )}

        {(state === S.INVALID || state === S.ERROR) && (
          <div className="w-full max-w-sm space-y-3">
            <div className="w-20 h-20 rounded-full bg-red-400/15 flex items-center justify-center mx-auto">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <div>
              <p className="text-3xl font-black text-red-400 mb-2">
                {state === S.INVALID ? 'Invalid ticket' : 'Error'}
              </p>
              <p className="text-red-300/70 text-sm">{errorMsg}</p>
            </div>
            {state === S.ERROR && (
              <button
                onClick={resetToScanning}
                className="border border-red-400/30 text-red-300 text-sm px-5 py-2 rounded-xl hover:bg-red-400/10 transition-colors"
              >
                Try again
              </button>
            )}
            {state !== S.ERROR && <p className="text-red-400/30 text-xs">Scanning again shortly…</p>}
          </div>
        )}
      </div>
    </div>
  );
}
